import React from 'react';
import { Activity, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/**
 * ProvocativeTestsPicker — Grouped picker of orthopaedic / neuro provocative
 * tests organized by body region.
 *
 * Feb 27, 2026 (per provider request): the prior tri-state cycling chip was
 * unreliable on mobile (mis-tap = wrong sign, no obvious "remove"). Replaced
 * with two explicit buttons per test:
 *
 *   [ + Test ]   [ − Test ]
 *
 * Behavior:
 *   - Tap `[+]` → marks the test POSITIVE (signed with `+`)
 *   - Tap `[+]` again on a positive test → REMOVES it
 *   - Tap `[−]` → marks the test NEGATIVE (signed with `−`)
 *   - Tap `[−]` again on a negative test → REMOVES it
 *   - Tap the OPPOSITE sign → switches sign (no extra remove tap needed)
 *
 * Tokens get appended to `peExceptions` so the downstream clinical-note
 * prompt knows whether each finding was positive or negative.
 */

const REGIONS = [
  {
    key: 'cervical',
    label: 'Cervical',
    tests: [
      'Spurling',
      'Lhermitte',
      'Hoffman',
      'Distraction',
      'ULTT (Upper Limb Tension)',
      'Cervical Compression',
    ],
  },
  {
    key: 'shoulder',
    label: 'Shoulder',
    tests: [
      'Hawkins-Kennedy',
      'Neer',
      'Empty Can (Jobe)',
      'Drop Arm',
      'Speed',
      'O\u2019Brien',
      'Apprehension',
      'Sulcus',
      'Cross-Body Adduction',
      'Lift-Off (Gerber)',
      'Belly-Press',
    ],
  },
  {
    key: 'elbow',
    label: 'Elbow',
    tests: [
      'Cozen',
      'Mill',
      'Maudsley',
      'Reverse Cozen (Medial Epi)',
      'Tinel (Cubital Tunnel)',
      'Elbow Flexion',
      'Valgus Stress (Elbow)',
      'Varus Stress (Elbow)',
      'Moving Valgus Stress',
    ],
  },
  {
    key: 'wrist',
    label: 'Wrist / Hand',
    tests: [
      'Tinel (Carpal Tunnel)',
      'Phalen',
      'Reverse Phalen',
      'Finkelstein',
      'Watson (Scaphoid Shift)',
      'Allen',
      'Carpal Compression (Durkan)',
      'Grind (CMC)',
      'TFCC Load',
    ],
  },
  {
    key: 'knee',
    label: 'Knee',
    tests: [
      'Lachman',
      'McMurray',
      'Anterior Drawer',
      'Posterior Drawer',
      'Valgus Stress',
      'Varus Stress',
      'Patellar Grind',
      'Apley Compression',
      'Thessaly',
    ],
  },
  {
    key: 'hip',
    label: 'Hip',
    tests: [
      'FABER (Patrick)',
      'FADIR',
      'Trendelenburg',
      'Thomas',
      'Ober',
      'Stinchfield (Resisted SLR)',
      'Log Roll',
    ],
  },
  {
    key: 'ankle',
    label: 'Ankle / Foot',
    tests: [
      'Anterior Drawer (Ankle)',
      'Talar Tilt',
      'Squeeze (Syndesmosis)',
      'Thompson',
      'Tinel (Tarsal Tunnel)',
      'Mulder',
    ],
  },
  {
    key: 'back',
    label: 'Back / Lumbar',
    tests: [
      'SLR (Straight Leg Raise)',
      'Crossed SLR',
      'Slump',
      'FABER (Sacroiliac)',
      'Gaenslen',
      'Sacroiliac Compression',
      'Schober',
      'Prone Instability',
    ],
  },
];

// Detect the current sign of a test inside the PE Exceptions textarea.
// Returns '+', '-', or null. Matches "+SLR" / "- SLR" / "neg SLR" / "+SLR right".
const detectSign = (text, testName) => {
  if (!text) return null;
  // Normalize: ignore parenthetical extras for matching
  const bare = testName.replace(/\s*\([^)]*\)\s*/g, '').trim();
  // Escape regex specials
  const esc = bare.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const reNeg = new RegExp(`(?:^|[\\s,;])\\s*[-\u2212]\\s*${esc}\\b`, 'i');
  const rePos = new RegExp(`(?:^|[\\s,;])\\s*\\+\\s*${esc}\\b`, 'i');
  if (reNeg.test(text)) return '-';
  if (rePos.test(text)) return '+';
  // Bare mention without sign — treat as positive (legacy behavior)
  const reBare = new RegExp(`(?:^|[\\s,;])${esc}\\b`, 'i');
  if (reBare.test(text)) return '+';
  return null;
};

const removeTestFromText = (text, testName) => {
  if (!text) return '';
  const bare = testName.replace(/\s*\([^)]*\)\s*/g, '').trim();
  const esc = bare.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Remove ", +Test" or "+Test, " or "-Test " etc.
  const re = new RegExp(`\\s*,?\\s*[+\\-\u2212]?\\s*${esc}\\b[^,\\n]*`, 'i');
  return text.replace(re, '').replace(/^\s*,\s*/, '').replace(/,\s*,/g, ',').trim();
};

const appendTestToText = (text, signed) => {
  const cur = (text || '').trim();
  if (!cur) return signed;
  return cur.endsWith(',') ? `${cur} ${signed}` : `${cur}, ${signed}`;
};

const ProvocativeTestsPicker = ({ peExceptions, setPeExceptions, markPeFindingsManual }) => {
  /**
   * Feb 28, 2026 — single-pill CYCLE behavior per user direction:
   *   - 1st tap on an untoggled pill  → POSITIVE
   *   - 2nd tap (already positive)    → NEGATIVE
   *   - 3rd tap (already negative)    → REMOVE
   * Drastically reduces visual noise vs the previous two-button (±) layout
   * and matches the user's "press it once = positive, twice = negative"
   * mental model.
   */
  const handleCycle = (testName) => {
    const cur = detectSign(peExceptions, testName);
    let next;
    if (cur === null) {
      // none → +
      next = appendTestToText(peExceptions || '', `+${testName}`);
    } else if (cur === '+') {
      // + → −  (remove first, then append with minus glyph)
      const cleaned = removeTestFromText(peExceptions, testName);
      next = appendTestToText(cleaned, `\u2212${testName}`);
    } else {
      // − → remove
      next = removeTestFromText(peExceptions, testName);
    }
    setPeExceptions(next);
    if (markPeFindingsManual) markPeFindingsManual();
  };

  // Count how many tests are currently selected across all regions
  const totalSelected = REGIONS.reduce((sum, r) => {
    return sum + r.tests.filter((t) => detectSign(peExceptions, t) !== null).length;
  }, 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-6 px-2 text-[10px] gap-1 border-amber-500/40 text-amber-300 hover:bg-amber-500/15 hover:text-amber-200 rounded-full"
          data-testid="provocative-tests-trigger"
          title="Add positive / negative provocative tests by body region"
        >
          <Activity className="w-3 h-3" />
          Provocative
          {totalSelected > 0 && (
            <span className="ml-0.5 px-1 rounded bg-amber-500/30 text-amber-100 text-[9px] font-bold">
              {totalSelected}
            </span>
          )}
          <ChevronDown className="w-3 h-3 opacity-70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(640px,92vw)] max-h-[70vh] overflow-y-auto p-3 bg-popover border border-amber-500/30"
        data-testid="provocative-tests-popover"
      >
        <div className="mb-2 pb-2 border-b border-border/60">
          <div className="text-[11px] font-bold text-amber-300 uppercase tracking-wider">
            Provocative Tests
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            Tap once for <span className="text-emerald-300 font-bold">+POSITIVE</span>, tap again for <span className="text-rose-300 font-bold">−NEGATIVE</span>, third tap removes.
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {REGIONS.map((region) => (
            <div key={region.key} data-testid={`provoc-region-${region.key}`}>
              {/* Feb 27, 2026 — region collapsed by default unless it already
                  has a selected test in it. Saves popover height when only
                  one region is in use. Click the region header to toggle. */}
              <details
                open={region.tests.some(t => detectSign(peExceptions, t) !== null)}
                className="group"
              >
                <summary className="text-[10px] font-bold uppercase tracking-wider text-blue-300/80 mb-1 cursor-pointer flex items-center gap-1.5 hover:text-blue-200 select-none">
                  <span className="inline-block group-open:rotate-90 transition-transform">▸</span>
                  {region.label}
                  {(() => {
                    const activeCount = region.tests.filter(t => detectSign(peExceptions, t) !== null).length;
                    return activeCount > 0 ? (
                      <span className="ml-1 px-1 rounded bg-emerald-500/30 text-emerald-100 text-[9px] font-bold">{activeCount}</span>
                    ) : null;
                  })()}
                </summary>
              {/* Feb 28, 2026 — wrap pills in flex so they auto-pack instead
                  of one-per-row. Sleek pill-cycle replaces the previous
                  chunky two-button (±) layout. */}
              <div className="flex flex-wrap gap-1 pt-1">
                {region.tests.map((t) => {
                  const sign = detectSign(peExceptions, t);
                  const isPositive = sign === '+';
                  const isNegative = sign === '-';
                  const slug = t.replace(/\s+/g, '-').replace(/[^a-z0-9-]/gi, '').toLowerCase();
                  // State-driven palette + glyph prefix
                  let palette =
                    'bg-secondary/40 border-border text-muted-foreground/90 hover:bg-amber-500/15 hover:border-amber-500/40 hover:text-amber-200';
                  let glyph = '';
                  let titleTxt = `Tap to mark "${t}" POSITIVE`;
                  if (isPositive) {
                    palette = 'bg-emerald-500/25 border-emerald-400/70 text-emerald-100 ring-1 ring-emerald-400/50';
                    glyph = '+';
                    titleTxt = `+${t} — tap to switch to NEGATIVE`;
                  } else if (isNegative) {
                    palette = 'bg-rose-500/25 border-rose-400/70 text-rose-100 ring-1 ring-rose-400/50';
                    glyph = '\u2212';
                    titleTxt = `−${t} — tap to remove`;
                  }
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => handleCycle(t)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium leading-tight transition-all ${palette}`}
                      title={titleTxt}
                      data-testid={`provoc-pill-${region.key}-${slug}`}
                      data-state={isPositive ? 'positive' : isNegative ? 'negative' : 'inactive'}
                    >
                      {glyph && <span className="font-bold">{glyph}</span>}
                      <span>{t}</span>
                    </button>
                  );
                })}
              </div>
              </details>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ProvocativeTestsPicker;
